import { pascalCase } from 'module/helpers/pascal-case';
import { unreachable } from 'module/helpers/unreachable';
import {
  AssigneeField,
  AssigneeValue,
} from 'module/types/filter-field/field/filterable-field';
import { kebabCase } from 'lodash';
import {
  SelectFilterOperationType,
  valueFromString,
  valueToString,
} from './select';
import { TermSeparator } from '../separators';

interface AssigneeFieldFilterBase {
  field: AssigneeField;
}

interface AssigneeFieldValuesFilterBase extends AssigneeFieldFilterBase {
  value?: AssigneeValue[];
}

interface AssigneeIsFilter extends AssigneeFieldValuesFilterBase {
  type: SelectFilterOperationType.Is;
}

interface AssigneeIsNotFilter extends AssigneeFieldValuesFilterBase {
  type: SelectFilterOperationType.IsNot;
}

interface AssigneeIsOneOfFilter extends AssigneeFieldValuesFilterBase {
  type: SelectFilterOperationType.IsOneOf;
}

interface AssigneeIsNoneOfFilter extends AssigneeFieldValuesFilterBase {
  type: SelectFilterOperationType.IsNoneOf;
}

interface AssigneeIsEmptyFilter extends AssigneeFieldFilterBase {
  type: SelectFilterOperationType.IsEmpty;
}

interface AssigneeIsNotEmptyFilter extends AssigneeFieldFilterBase {
  type: SelectFilterOperationType.IsNotEmpty;
}

export type AssigneeFieldFilter =
  | AssigneeIsFilter
  | AssigneeIsNotFilter
  | AssigneeIsOneOfFilter
  | AssigneeIsNoneOfFilter
  | AssigneeIsEmptyFilter
  | AssigneeIsNotEmptyFilter;

const isValid = (filter: AssigneeFieldFilter) => {
  switch (filter.type) {
    case SelectFilterOperationType.Is:
    case SelectFilterOperationType.IsNot:
    case SelectFilterOperationType.IsOneOf:
    case SelectFilterOperationType.IsNoneOf:
      return (filter.value ?? []).length > 0;
    default:
      return true;
  }
};

const to = valueToString<AssigneeValue>((x) => x.join('-'));
const from = valueFromString((x) => x.split('-') as AssigneeValue);

const toString = (filter: AssigneeFieldFilter): string => {
  const type = kebabCase(filter.type);

  switch (filter.type) {
    case SelectFilterOperationType.IsEmpty:
    case SelectFilterOperationType.IsNotEmpty:
      return type;
    case SelectFilterOperationType.Is:
    case SelectFilterOperationType.IsNot:
    case SelectFilterOperationType.IsNoneOf:
    case SelectFilterOperationType.IsOneOf:
      return [type, to(filter.value ?? [])].join(TermSeparator);
    default:
      return unreachable(filter);
  }
};

const parse = (value: string, field: AssigneeField): AssigneeFieldFilter => {
  const [type_string, meta] = value.split(TermSeparator);
  const type = pascalCase(type_string) as SelectFilterOperationType;

  switch (type) {
    case SelectFilterOperationType.IsEmpty:
    case SelectFilterOperationType.IsNotEmpty:
      return { field, type };
    case SelectFilterOperationType.Is:
    case SelectFilterOperationType.IsNot:
    case SelectFilterOperationType.IsOneOf:
    case SelectFilterOperationType.IsNoneOf:
      return { field, type, value: from(meta) };
    case SelectFilterOperationType.IsAtLeast:
    case SelectFilterOperationType.IsAtMost:
    default:
      return unreachable(
        `${type} cannot be parsed for ${field.type} field` as never,
      );
  }
};

export const AssigneeFieldFilter = {
  isValid,
  toString,
  parse,
};
